import { useEffect, useRef } from "react";

interface UseInfiniteScrollSentinelParams {
  /** Whether more items can be loaded from the server. */
  hasMore?: boolean;
  /** Fired when the returned sentinel element scrolls into view. */
  onLoadMore?: () => void;
}

/**
 * Returns a ref to attach to a sentinel element rendered at the end of a list. When that
 * element scrolls into view and `hasMore` is set, `onLoadMore` fires. Uses an
 * IntersectionObserver so it works regardless of which ancestor is the scroll container
 * (avoids Virtuoso's useWindowScroll limitation).
 */
const useInfiniteScrollSentinel = <T extends HTMLElement>({
  hasMore,
  onLoadMore,
}: UseInfiniteScrollSentinelParams) => {
  const sentinelRef = useRef<T>(null);

  useEffect(() => {
    if (!hasMore || !onLoadMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  return sentinelRef;
};

export default useInfiniteScrollSentinel;
